import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";

const TokenAuth: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken, isLoading } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);
  const requestedCurrency = searchParams.get("displayCurrency")?.toUpperCase();
  const currencyQuery =
    requestedCurrency && /^[A-Z]{3}$/.test(requestedCurrency)
      ? `?displayCurrency=${encodeURIComponent(requestedCurrency)}`
      : "";

  useEffect(() => {
    if (hasAttempted) {
      return;
    }

    const token =
      searchParams.get("token") ||
      searchParams.get("access_token") ||
      (() => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get("token");
      })() ||
      (() => {
        if (!window.location.hash) return null;
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1),
        );
        return hashParams.get("token");
      })();

    setHasAttempted(true);

    if (token && !isProcessing) {
      setIsProcessing(true);
      handleTokenLogin(token);
    } else if (!token) {
      toast.error(t("toast.noAuthToken"));
      setTimeout(() => {
        window.close();
      }, 3000);
    }
  }, [hasAttempted]);

  const handleTokenLogin = async (token: string) => {
    try {
      // Reject links that explicitly identify a non-dealer account.
      let decodedRole: string | undefined;
      let linkType: string | undefined;
      let purpose: string | undefined;

      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        decodedRole = payload?.role || payload?.userType;
        linkType = payload?.linkType;
        purpose = payload?.purpose;

        if (decodedRole && decodedRole !== "dealer") {
          toast.error(t("toast.onlyDealerAccounts"));
          setTimeout(() => window.close(), 2500);
          return;
        }
      } catch {
        console.warn(
          "The link token could not be decoded locally; validating it with the API.",
        );
      }

      const user = await loginWithToken(token);

      if (user?.role !== "dealer") {
        console.error("Token authentication returned a non-dealer account.");
        toast.error(t("toast.onlyDealerAccounts"));
        setTimeout(() => window.close(), 2500);
        return;
      }

      if (user?.role === "dealer") {
        // Support both the legacy and current activation fields.
        const isActivated =
          user?.isActive === true || user?.activationStatus === "activated";

        if (linkType === "activation" || purpose === "dealer_activation") {
          navigate(`/dealer/activation${currencyQuery}`);
        } else if (isActivated) {
          navigate(`/deposit${currencyQuery}`);
        } else {
          navigate(`/dealer/activation${currencyQuery}`);
        }
      } else {
        navigate(`/deposit${currencyQuery}`);
      }
    } catch (error: any) {
      toast.error(error.message || t("toast.authFailed"));
      setTimeout(() => {
        window.close();
      }, 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600"></div>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-secondary">
            {isProcessing
              ? t("auth.authenticating")
              : t("auth.processingAuthentication")}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{t("auth.pleaseWait")}</p>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full animate-pulse"
            style={{ width: "70%" }}
          ></div>
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            {t("auth.epointDepositSystem")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TokenAuth;
